import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Check, Settings } from 'lucide';
import { lucideIcon } from '../icon.js';

/**
 * <column-picker> — het tandwiel waarmee je kolommen aan- en uitzet.
 *
 * Gebruik:
 *   <column-picker
 *     .options=${[{ id: 'naam', label: 'Naam' }, { id: 'datum', label: 'Datum', vast: true }]}
 *     .visible=${['naam']}
 *     @change=${e => bewaar(e.detail.visible)}>
 *   </column-picker>
 *
 * `vast: true` zet een kolom vast: hij staat in de lijst met een vinkje dat je niet uit kunt
 * zetten. Bedoeld voor de kolom waar de tabel om draait -- die weghalen levert een tabel op
 * waar je niets meer mee kunt.
 *
 * De component bewaart niets: de eigenaar weet waar de voorkeur hoort (bij deze gebruiker, op
 * de server) en krijgt bij elke wijziging de volledige lijst zichtbare ids terug, in de
 * volgorde van `options` -- zodat kolommen niet verspringen op aanklikvolgorde.
 *
 * Het menu staat `position:fixed` en wordt bij openen op de knop gepositioneerd: een tabel zit
 * vaak in iets dat afknipt (ronde hoeken, horizontaal schuiven) en binnen die stroom zou het
 * menu worden afgesneden.
 *
 * NB: mv-table heeft een eigen, oudere kopie van deze kiezer, verweven met zijn kolomkoppen.
 * Die is met opzet niet aangepast toen deze component er kwam; wie daar toch aan werkt kan hem
 * hiermee vervangen.
 */
export class ColumnPicker extends LitElement {
  static properties = {
    options: { type: Array },
    visible: { type: Array },
    label: { type: String },
    _open: { state: true },
    _pos: { state: true },
  };

  constructor() {
    super();
    this.options = [];
    this.visible = [];
    this.label = 'Kolommen tonen';
    this._open = false;
    this._pos = null;
    this._scrollers = [];
    this._sluit = () => { this._open = false; this._ontkoppelScroll(); };
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this._sluit);
    window.addEventListener('resize', this._sluit);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._sluit);
    window.removeEventListener('resize', this._sluit);
    this._ontkoppelScroll();
    super.disconnectedCallback();
  }

  /**
   * Meeluisteren met alles wat kan schuiven boven deze knop.
   *
   * Het menu staat op een vaste plek in het venster, dus zodra er iets schuift klopt die plek
   * niet meer: dan hangt het menu los van het tandwiel. Sluiten dus. Eén luisteraar op
   * `document` is daarvoor niet genoeg -- scroll-events zijn niet `composed` en komen dus niet
   * uit een schaduwboom naar buiten, en in deze app schuift juist een container binnen de
   * schaduwboom van de omhulzende pagina. Vandaar het samengestelde pad van de klik: daar
   * staat elke voorouder in, dwars door de schaduwgrenzen heen.
   */
  _koppelScroll(pad) {
    this._ontkoppelScroll();
    this._scrollers = pad.filter(n => n === window || n === document || n instanceof Element);
    for (const n of this._scrollers) n.addEventListener('scroll', this._sluit, { passive: true });
  }

  _ontkoppelScroll() {
    for (const n of this._scrollers || []) n.removeEventListener('scroll', this._sluit);
    this._scrollers = [];
  }

  _wissel(optie) {
    if (optie.vast) return;
    const aan = new Set(this.visible || []);
    aan.has(optie.id) ? aan.delete(optie.id) : aan.add(optie.id);
    const volgende = (this.options || []).map(o => o.id).filter(id => aan.has(id));
    this.dispatchEvent(new CustomEvent('change', {
      detail: { visible: volgende }, bubbles: true, composed: true,
    }));
  }

  /**
   * Openen, en zorgen dat het menu in beeld past.
   *
   * Op een korte pagina -- een project met drie labopdrachten -- viel de onderkant van het menu
   * buiten het venster, en omdat er niets te scrollen viel kwam je er niet bij. Daarom: hoogte
   * begrenzen op wat eronder past, en is dat te weinig, dan klapt hij boven de knop uit. Blijft
   * er dan nog te weinig over, dan schuift het menu zelf (max-height + overflow).
   */
  _open_(e) {
    if (this._open) { this._sluit(); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const marge = 12;
    const eronder = window.innerHeight - r.bottom - marge;
    const erboven = r.top - marge;
    const omhoog = eronder < 180 && erboven > eronder;
    this._pos = {
      right: Math.round(window.innerWidth - r.right),
      ...(omhoog
        ? { bottom: Math.round(window.innerHeight - r.top + 6), max: Math.round(erboven) }
        : { top: Math.round(r.bottom + 6), max: Math.round(eronder) }),
    };
    this._open = true;
    this._koppelScroll(e.composedPath());
  }

  /** De plek en de hoogtegrens als inline stijl: allemaal gemeten waarden, geen klassen. */
  get _menuStijl() {
    const p = this._pos || {};
    const verticaal = p.bottom != null ? `bottom:${p.bottom}px` : `top:${p.top ?? 0}px`;
    return `${verticaal}; right:${p.right ?? 0}px; max-height:${Math.max(p.max ?? 320, 140)}px`;
  }

  render() {
    const aan = new Set(this.visible || []);
    return html`
      <div class="wrap" @click=${(e) => e.stopPropagation()}>
        <button class="knop" title="Kolommen kiezen" aria-haspopup="true"
                aria-expanded=${this._open ? 'true' : 'false'} @click=${this._open_}>
          ${unsafeSVG(lucideIcon(Settings, 15))}
        </button>
        ${this._open ? html`
          <div class="menu" style=${this._menuStijl}>
            <div class="kop">${this.label}</div>
            <div class="lijst">
            ${(this.options || []).map((o) => {
              const gekozen = o.vast || aan.has(o.id);
              return html`
                <button class="regel ${o.vast ? 'vast' : ''}" role="menuitemcheckbox"
                        aria-checked=${gekozen ? 'true' : 'false'} ?disabled=${o.vast}
                        title=${o.vast ? 'Deze kolom staat vast' : ''}
                        @click=${() => this._wissel(o)}>
                  <span class="vak ${gekozen ? 'aan' : ''}">
                    ${gekozen ? unsafeSVG(lucideIcon(Check, 12)) : ''}
                  </span>
                  <span>${o.label}</span>
                </button>`;
            })}
            </div>
          </div>` : ''}
      </div>
    `;
  }

  static styles = css`
    :host { display:inline-flex; }
    .wrap { display:inline-flex; }
    .knop { display:flex; padding:4px; border:none; border-radius:6px; background:none;
            color:var(--mv-text-muted); cursor:pointer; }
    .knop:hover { background:var(--mv-surface-alt); color:var(--mv-text); }
    /* Schuiven gebeurt in de LIJST, niet in het menu: met een kop die vastgeplakt zat schoof
       een regel onder de kop door de padding van het menu -- dan staat er een halve kolomnaam
       bóven 'Kolommen tonen'. Nu is de kop een gewone buur van het schuifvak en kan er niets
       achter langs.
       box-sizing: anders telt max-height alleen de inhoud en steekt het menu met zijn eigen
       padding en rand alsnog onder het venster uit. */
    .menu { box-sizing:border-box; position:fixed; z-index:1300; min-width:220px; padding:6px;
            display:flex; flex-direction:column; overflow:hidden;
            border:1px solid var(--mv-border); border-radius:10px; background:var(--mv-surface);
            box-shadow:0 12px 32px rgba(15,23,42,.16); }
    .lijst { overflow-y:auto; overscroll-behavior:contain; }
    .kop { padding:7px 10px 6px; font-size:0.64rem; font-weight:800; letter-spacing:.06em;
           text-transform:uppercase; color:var(--mv-text-muted); }
    .regel { display:flex; align-items:center; gap:10px; width:100%; padding:7px 10px;
             border:none; border-radius:7px; background:none; color:var(--mv-text);
             font:inherit; font-size:0.84rem; text-align:left; cursor:pointer; }
    .regel:hover:not(:disabled) { background:var(--mv-surface-alt); }
    .regel.vast { cursor:default; color:var(--mv-text-sub); }
    .vak { display:flex; align-items:center; justify-content:center; flex:none;
           width:17px; height:17px; border:1.5px solid var(--mv-border); border-radius:5px;
           color:#fff; }
    .vak.aan { border-color:var(--mv-primary); background:var(--mv-primary); }
    .regel.vast .vak.aan { opacity:.55; }
  `;
}

customElements.define('column-picker', ColumnPicker);
